import moment from "moment";
import { toTitleCase } from "../../utils/helperFunctions";

// Releases whose labels can still be patched from the summary table.
export const PATCHABLE_RELEASE_STATUSES = ["CREATED", "OUT_OF_RELEASE_CYCLE"];

export const MAX_VISIBLE_LABELS = 4;

/**
 * Age of an ISO timestamp, in days up to a year and months beyond it.
 * @param {string} dateCreated
 * @returns {string} */
export const formatAge = (dateCreated) => {
  const created = moment(dateCreated);
  if (!dateCreated || !created.isValid()) return "";
  const days = moment().diff(created, "days");
  if (days < 365) return `${Math.max(days, 0)} d`;
  return `${moment().diff(created, "months")} mo`;
};

/**
 * @param {string} dateCreated
 * @returns {string} */
export const formatLabelDate = (dateCreated) => {
  const created = moment(dateCreated);
  return dateCreated && created.isValid() ? created.format("D MMM YYYY") : "";
};

const RELEASE_LABEL = /^rel-\d+\.(\d+)\.\d+$/;

/**
 * Release labels reach the release/service views in their raw RMS form
 * ("rel-0.148.0"). Filters read better as "rel-148", matching how releases are
 * named everywhere else; the raw label stays the value, so only what the user
 * reads changes.
 * @param {string} label
 * @returns {string} */
export const releaseOptionLabel = (label = "") => {
  const match = RELEASE_LABEL.exec(label || "");
  return match ? `rel-${match[1]}` : label;
};

// The only label actions that move a label forward; everything else lives in the row menu.
const FORWARD_LABEL_ACTIONS = ["DEPLOY_TO_QA", "DEPLOY_TO_STAGE"];

/**
 * @param {string[]} actionsAllowed
 * @returns {string|null} */
export const pickForwardLabelAction = (actionsAllowed = []) =>
  FORWARD_LABEL_ACTIONS.find((action) => actionsAllowed?.includes(action)) ||
  null;

/**
 * Lifecycle action shown in the labels drawer header.
 * @param {string[]} actionsAllowed
 * @returns {string|null} */
export const pickReleaseDrawerAction = (actionsAllowed = []) => {
  if (actionsAllowed?.includes("ARCHIVE")) return "ARCHIVE";
  if (actionsAllowed?.includes("DELETE")) return "DELETE";
  return null;
};

const byDateCreatedDesc = (a, b) =>
  moment(b?.dateCreated).valueOf() - moment(a?.dateCreated).valueOf();

/**
 * Labels split by lifecycle state, newest first within every group.
 * @param {any[]} releaseLabels
 * @returns {{active: any[], inProduction: any[], failed: any[], discarded: any[]}} */
export const groupReleaseLabels = (releaseLabels = []) => {
  const labels = [...(releaseLabels || [])].sort(byDateCreatedDesc);
  const statusOf = (label) => String(label?.status || "").toUpperCase();
  const rest = labels.filter((label) => !label?.highlight);
  const isTerminal = (label) => {
    const status = statusOf(label);
    return (
      status === "IN_PRODUCTION" ||
      status.endsWith("_FAILED") ||
      status.startsWith("DISCARDED")
    );
  };
  return {
    active: labels.filter((label) => label?.highlight),
    inProduction: rest.filter((label) => statusOf(label) === "IN_PRODUCTION"),
    failed: rest.filter((label) => statusOf(label).endsWith("_FAILED")),
    discarded: rest.filter((label) => statusOf(label).startsWith("DISCARDED")),
    // Catch-all. A label the API does not highlight and that has not reached a
    // terminal state — a superseded READY_FOR_* patch, say — matches none of
    // the groups above and would otherwise be counted in the header but shown
    // nowhere. Every label has to land in exactly one bucket.
    superseded: rest.filter((label) => !isTerminal(label)),
  };
};

/**
 * Oldest to newest version span of an already sorted (desc) group.
 * @param {any[]} labels
 * @returns {string} */
export const versionRange = (labels = []) => {
  if (!labels.length) return "";
  const newest = labels[0]?.name;
  const oldest = labels[labels.length - 1]?.name;
  return newest === oldest ? newest : `${oldest} \u2013 ${newest}`;
};

const LABEL_STATUS_TEXT = {
  READY_FOR_QA_DEPLOYMENT: "Ready for QA",
  READY_FOR_STAGE_DEPLOYMENT: "Ready for stage",
  READY_FOR_STAGE_VALIDATION: "Ready for stage validation",
  IN_PRODUCTION: "In production",
  STAGE_DEPLOYMENT_FAILED: "Stage deploy failed",
  STAGE_VALIDATION_FAILED: "Stage validation failed",
  QA_VALIDATION_FAILED: "QA validation failed",
};

/**
 * @param {string} status
 * @returns {string} */
export const labelStatusText = (status = "") => {
  const value = String(status).toUpperCase();
  if (LABEL_STATUS_TEXT[value]) return LABEL_STATUS_TEXT[value];
  if (value.startsWith("DISCARDED")) return "Discarded";
  return toTitleCase(value).replace("Qa", "QA");
};

const ACTION_TEXT = {
  DEPLOY_TO_QA: "Deploy to QA",
  DEPLOY_TO_STAGE: "Deploy to stage",
  MARK_AS_UNDER_STAGE_VALIDATION: "Validate",
  MARK_AS_READY_FOR_STAGE_DEPLOYMENT: "Mark ready for stage",
  CREATE: "Create",
  MARK_CREATED: "Mark created",
  MARK_CREATION_AS_FAILED: "Mark creation failed",
  ARCHIVE: "Archive",
  DISCARD: "Discard",
  DELETE: "Delete",
};

/**
 * @param {string} action
 * @param {string} status status of the entity the action belongs to
 * @returns {string} */
export const actionText = (action = "", status = "") => {
  if (
    action === "DEPLOY_TO_STAGE" &&
    String(status).toUpperCase() === "STAGE_DEPLOYMENT_FAILED"
  ) {
    return "Retry";
  }
  return ACTION_TEXT[action] || toTitleCase(action);
};

/**
 * @param {string} action
 * @returns {boolean} */
export const isDestructiveAction = (action = "") =>
  action.startsWith("DISCARD") || action === "DELETE";

// Only one label action fits inside a pill; the rest move to the row menu.
const LABEL_ACTION_PRIORITY = [
  "DEPLOY_TO_QA",
  "DEPLOY_TO_STAGE",
  "MARK_AS_UNDER_STAGE_VALIDATION",
  "MARK_AS_READY_FOR_STAGE_DEPLOYMENT",
];

/**
 * @param {string[]} actionsAllowed
 * @returns {string|null} */
export const pickLabelAction = (actionsAllowed = []) =>
  LABEL_ACTION_PRIORITY.find((action) => actionsAllowed?.includes(action)) ||
  null;

/**
 * Release lifecycle action to promote to the row button; null means "Patch".
 * @param {string[]} actionsAllowed
 * @returns {string|null} */
export const pickReleaseAction = (actionsAllowed = []) => {
  if (actionsAllowed?.includes("CREATE")) return "CREATE";
  if (actionsAllowed?.includes("MARK_CREATED")) return "MARK_CREATED";
  return null;
};

/**
 * Label history counts derived from the full label list.
 * @param {any[]} releaseLabels
 * @returns {{total: number, discarded: number, failed: number}|null} */
export const labelCounts = (releaseLabels) => {
  if (!Array.isArray(releaseLabels)) return null;
  const statuses = releaseLabels.map((label) =>
    String(label?.status || "").toUpperCase()
  );
  return {
    total: statuses.length,
    discarded: statuses.filter((status) => status.startsWith("DISCARDED"))
      .length,
    failed: statuses.filter((status) => status.endsWith("_FAILED")).length,
  };
};

const SEMVER = /^(\d+)\.(\d+)\.(\d+)$/;

/**
 * Label the next patch of a release would get, from the labels it already has.
 * @param {any} release
 * @returns {string} */
export const nextPatchLabel = (release) => {
  const parsed = (release?.releaseLabels || [])
    .map((label) => SEMVER.exec(label?.name || ""))
    .filter(Boolean);
  if (parsed.length) {
    const highest = parsed.reduce(
      (max, match) => Math.max(max, Number(match[3])),
      0
    );
    return `${parsed[0][1]}.${parsed[0][2]}.${highest + 1}`;
  }
  const minor = String(release?.name || "").split("-")[1];
  return minor ? `0.${minor}.1` : "";
};

/**
 * @param {any[]} releases
 * @returns {{open: number, archived: number, newest: any}} */
export const summaryStats = (releases = []) => {
  const open = releases.filter((release) => release?.status !== "ARCHIVED");
  const newest = open.reduce(
    (latest, release) =>
      moment(release?.dateCreated).isAfter(moment(latest?.dateCreated))
        ? release
        : latest,
    open[0]
  );
  return {
    open: open.length,
    archived: releases.length - open.length,
    newest,
  };
};
